import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import toast from "react-hot-toast";
import validator from "validator";
import axios from "axios";

export default function DeleteAccount(): React.ReactElement {
    const [token, setToken] = useState("");
    const [deviceId, setDeviceId] = useState("");
    const [loading, setLoading] = useState(false);
    const [phoneError, setPhoneError] = useState("");
    const [resendTimer, setResendTimer] = useState(0);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [otpMethod, setOtpMethod] = useState<"whatsapp" | "sms">("whatsapp");
    const [confirmText, setConfirmText] = useState("");
    const [otpLoading, setOtpLoading] = useState(false);
    const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showOTPSection, setShowOTPSection] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimer]);

    const getDeviceInfo = () => {
        const parser = new UAParser();
        const result = parser.getResult();

        // Get device name (brand + model or OS name)
        let deviceName = "";
        if (result.device.vendor && result.device.model) {
            deviceName = `${result.device.vendor} ${result.device.model}`;
        } else if (result.os.name) {
            deviceName = result.os.name;
        } else {
            deviceName = "Unknown Device";
        }

        // Get device type
        let deviceType = "desktop"; // default
        if (result.device.type) {
            deviceType = result.device.type;
        } else if (result.os.name === "iOS" || result.os.name === "Android") {
            deviceType = "mobile";
        }

        return {
            name: deviceName,
            type: deviceType
        };
    };

    const validatePhoneNumber = (phone: string) => {
        if (validator.isEmpty(phone)) {
            setPhoneError("Phone number is required");
            return false;
        }
        if (!validator.isMobilePhone(phone, "en-IN")) {
            setPhoneError("Please enter a valid 10-digit phone number");
            return false;
        }
        setPhoneError("");
        return true;
    };

    const handleOtpChange = (index: number, value: string) => {
        if (value.length > 1) {
            const pastedData = value.slice(0, 6);
            const newOtp = [...otp];
            for (let i = 0; i < pastedData.length && i + index < 6; i++) {
                newOtp[index + i] = pastedData[i];
            }
            setOtp(newOtp);

            const nextIndex = Math.min(index + pastedData.length, 5);
            otpRefs.current[nextIndex]?.focus();
        } else {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value && index < 5) {
                otpRefs.current[index + 1]?.focus();
            }
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const onSendOTP = async () => {
        const isPhoneValid = validatePhoneNumber(phoneNumber);

        if (!isPhoneValid) {
            return;
        }

        setLoading(true);
        try {
            const deviceInfo = getDeviceInfo();

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/auth/send-otp`, {
                phone_number: phoneNumber,
                device_info: deviceInfo,
                is_account_deletion: true,
                otp_method: otpMethod
            });

            setDeviceId(response.data.data.device_id);
            toast.success("Verification code sent to your phone");
            setShowOTPSection(true);
            setResendTimer(30);
        } catch (error: any) {
            console.error("OTP Send Error Response:", error?.response?.data || error);
            toast.error(error?.response?.data?.message || "Unable to send verification code. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const onVerifyOTP = async () => {
        const otpCode = otp.join("");
        if (otpCode.length !== 6) {
            toast.error("Please enter the complete 6-digit code");
            return;
        }

        setOtpLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/auth/verify-otp`, {
                phone_number: phoneNumber,
                otp_code: otpCode,
                device_id: deviceId
            });

            toast.success("Phone number verified successfully");
            setToken(response.data.data.access_token);
            setShowDeleteConfirm(true);
        } catch (error: any) {
            console.error("OTP Verify Error Response:", error?.response?.data || error);
            toast.error(error?.response?.data?.message || "Incorrect verification code. Please try again.");
        } finally {
            setOtpLoading(false);
        }
    };

    const onDeleteAccount = async () => {
        if (confirmText !== "DELETE") {
            toast.error('Please type "DELETE" exactly as shown to confirm');
            return;
        }

        setDeleteLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/user/auth/delete-account`, {
                phone_number: phoneNumber,
                device_id: deviceId
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            toast.success("Your account has been permanently deleted");

            // Reset form after successful deletion
            setTimeout(() => {
                setToken("");
                setConfirmText("");
                setPhoneNumber("");
                setShowOTPSection(false);
                setShowDeleteConfirm(false);
                setOtp(["", "", "", "", "", ""]);
            }, 10);
        } catch (error: any) {
            console.error("Account Delete Error Response:", error?.response?.data || error);
            toast.error(error?.response?.data?.message || "Unable to delete account. Please try again or contact support.");
        } finally {
            setDeleteLoading(false);
        }
    };

    const onChangePhoneNumber = () => {
        setShowOTPSection(false);
        setShowDeleteConfirm(false);
        setOtp(["", "", "", "", "", ""]);
        setPhoneError("");
        setResendTimer(0);
        setConfirmText("");
    };

    const isOtpComplete = otp.every(digit => digit !== "");

    return (
        <div className="">
            <div className="min-h-[calc(100vh-70px)] flex flex-col items-center justify-center py-6 px-4">
                <div className="max-w-[480px] w-full">
                    <div className="p-4 sm:px-8 py-12 rounded-2xl bg-white border border-gray-200 shadow-sm">
                        <div className="text-center flex flex-col items-center space-y-2">
                            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                                </svg>
                            </div>
                            <h1 className="text-slate-900 text-center text-3xl font-semibold">
                                Delete Account
                            </h1>
                            <p className="text-slate-600 text-sm max-w-md">
                                {showDeleteConfirm
                                    ? "This action will permanently remove your account and all associated data."
                                    : "To proceed with account deletion, please verify your phone number."
                                }
                            </p>
                        </div>

                        <form className="mt-6 space-y-5" onSubmit={(e) => e.preventDefault()}>
                            {/* Phone Number Section */}
                            {!showDeleteConfirm && (
                                <div>
                                    {showOTPSection ? (
                                        <div className="text-center space-y-4">
                                            <div className="text-center">
                                                <p className="text-slate-600 text-center text-sm">
                                                    Verification code sent to <span className="text-slate-900 font-semibold">+91 {phoneNumber}</span>
                                                </p>
                                            </div>
                                            {/* <button
                                                type="button"
                                                onClick={onChangePhoneNumber}
                                                className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600 font-medium"
                                            >
                                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 17l-5-5m0 0l5-5m-5 5h12"></path>
                                                </svg>
                                                Change phone number
                                            </button> */}
                                        </div>
                                    ) : (
                                        <>
                                            <label className="text-slate-900 text-sm font-medium mb-2 block">
                                                Phone Number
                                            </label>

                                            <div className="relative flex items-center">
                                                <span className="absolute border-r pr-1.5 left-2 top-1/2 -translate-y-1/2 text-gray-500">
                                                    +91
                                                </span>
                                                <input
                                                    name="phoneNumber"
                                                    type="tel"
                                                    value={phoneNumber}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D+/g, '');
                                                        setPhoneNumber(value);
                                                        if (phoneError) {
                                                            validatePhoneNumber(value);
                                                        }
                                                    }}
                                                    onBlur={() => validatePhoneNumber(phoneNumber)}
                                                    className={`w-full pl-12 pr-10 text-slate-900 border px-4 py-2.5 rounded-md outline-primary-600 ${phoneError
                                                        ? 'border-red-500 focus:border-red-500'
                                                        : 'border-slate-300'
                                                        }`}
                                                    placeholder="Enter your 10-digit number"
                                                    maxLength={10}
                                                    pattern="[0-9]*"
                                                    inputMode="numeric"
                                                />
                                            </div>
                                            <div className="mt-2 flex items-center gap-3 text-sm">
                                                <span className="text-slate-500">Send code via:</span>
                                                <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="otpMethod" checked={otpMethod === "whatsapp"} onChange={() => setOtpMethod("whatsapp")} /> WhatsApp</label>
                                                <label className="flex items-center gap-1 cursor-pointer"><input type="radio" name="otpMethod" checked={otpMethod === "sms"} onChange={() => setOtpMethod("sms")} /> SMS</label>
                                            </div>
                                            {phoneError && (
                                                <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}

                            {/* OTP Section */}
                            {showOTPSection && !showDeleteConfirm && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-slate-900 text-sm font-medium mb-4 block text-center">
                                            Enter the 6-digit verification code
                                        </label>
                                        <div className="flex gap-2 md:gap-3 justify-center mb-6">
                                            {otp.map((digit, index) => (
                                                <input
                                                    key={index}
                                                    ref={(el) => {
                                                        otpRefs.current[index] = el;
                                                    }}
                                                    type="text"
                                                    value={digit}
                                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                    className="h-10 w-10 md:w-14 md:h-14 text-center text-xl font-bold text-slate-900 border-2 border-slate-300 rounded-xl outline-none focus:border-primary-600 focus:ring-2 focus:ring-primary-100 transition-all duration-200 hover:border-slate-400"
                                                    maxLength={6}
                                                    inputMode="numeric"
                                                />
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-center text-center gap-4">
                                            <p className="text-sm text-slate-500">
                                                Didn't receive the code?
                                            </p>
                                            <button
                                                type="button"
                                                onClick={onSendOTP}
                                                disabled={loading || resendTimer > 0}
                                                className="inline-flex items-center text-sm text-blue-500 hover:text-blue-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                                                </svg>
                                                {loading ? "Sending..." : resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend Code"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Delete Confirmation Section */}
                            {showDeleteConfirm && (
                                <div className="space-y-5">
                                    <p className="text-slate-600 text-center text-sm">
                                        Account to be deleted: <span className="text-slate-900 font-semibold">+91 {phoneNumber}</span>
                                    </p>
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <div className="flex items-start">
                                            <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"></path>
                                            </svg>
                                            <div className="text-sm text-red-800">
                                                <p className="font-semibold mb-2">Warning: This action cannot be reversed</p>
                                                <ul className="list-disc list-inside space-y-1 text-red-700">
                                                    <li>All your account data will be permanently deleted</li>
                                                    <li>You will not be able to recover your account or data</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-slate-900 text-sm font-medium mb-2 block">
                                            Type <span className="font-bold text-red-600">DELETE</span> to confirm deletion
                                        </label>
                                        <input
                                            type="text"
                                            value={confirmText}
                                            onChange={(e) => setConfirmText(e.target.value)}
                                            className="w-full text-slate-900 border border-slate-300 px-4 py-2.5 rounded-md outline-red-600"
                                            placeholder="Type DELETE"
                                        />
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={onChangePhoneNumber}
                                            className="flex-1 py-3 px-4 text-base font-semibold tracking-wide rounded-xl text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all duration-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            disabled={deleteLoading || confirmText !== "DELETE"}
                                            onClick={onDeleteAccount}
                                            className={`flex-1 py-3 px-4 text-base font-semibold tracking-wide rounded-xl text-white transition-all duration-200 ${deleteLoading || confirmText !== "DELETE"
                                                ? "bg-red-400 cursor-not-allowed"
                                                : "bg-red-600 hover:bg-red-700 hover:shadow-lg transform hover:-translate-y-0.5"
                                                }`}
                                        >
                                            {deleteLoading ? "Deleting..." : "Delete Account"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Action Button */}
                            {!showDeleteConfirm && (
                                <div className="!mt-7">
                                    {showOTPSection ? (
                                        <button
                                            type="button"
                                            disabled={otpLoading || !isOtpComplete}
                                            onClick={onVerifyOTP}
                                            className={`w-full py-3 px-4 text-base font-semibold tracking-wide rounded-xl text-white transition-all duration-200 ${otpLoading || !isOtpComplete
                                                ? "bg-primary-400 cursor-not-allowed"
                                                : "bg-primary-600 hover:bg-primary-700 hover:shadow-lg transform hover:-translate-y-0.5"
                                                }`}
                                        >
                                            {otpLoading ? "Verifying..." : "Verify & Continue"}
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={loading}
                                            onClick={onSendOTP}
                                            className={`w-full py-3 px-4 text-base font-semibold tracking-wide rounded-xl text-white transition-all duration-200 ${loading
                                                ? "bg-primary-400 cursor-not-allowed"
                                                : "bg-primary-600 hover:bg-primary-700 hover:shadow-lg transform hover:-translate-y-0.5"
                                                }`}
                                        >
                                            {loading ? "Sending OTP..." : "Send Verification Code"}
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="text-center mt-6 text-sm text-slate-600">
                                <p>
                                    For more information, please review our{" "}
                                    <Link to="/privacy-policy" className="text-primary-600 hover:underline">
                                        Privacy Policy
                                    </Link>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}